import { useCallback, useEffect, useState } from 'react';
import { api } from '../../../../services/api';
import type { FaturaCte, FaturasResposta, PreviaFatura, PreviaResposta } from './types';

export function useFaturas() {
  const [faturas, setFaturas] = useState<FaturaCte[]>([]);
  const [previas, setPrevias] = useState<PreviaFatura[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    // `allSettled`, não `all`: **falha da prévia não pode derrubar a lista.**
    // A lista de faturas é o conteúdo da tela e existe desde sempre; a prévia é
    // acréscimo, e um timeout nela não pode apagar o que já funcionava.
    const [rFaturas, rPrevia] = await Promise.allSettled([
      api.get<FaturasResposta>('/cooperado/faturas', { params: { limit: 50 } }),
      api.get<PreviaResposta>('/cooperado/faturas/previa'),
    ]);

    if (rFaturas.status === 'fulfilled') {
      setFaturas(rFaturas.value.data.data);
      setTotal(rFaturas.value.data.total);
    } else {
      setErro('Não foi possível carregar suas faturas. Verifique a conexão e tente novamente.');
    }

    // Prévia sem valor não é informação: é uma linha dizendo "zero".
    setPrevias(rPrevia.status === 'fulfilled'
      ? rPrevia.value.data.data.filter(p => p.valorIcms > 0)
      : []);

    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  /**
   * As faturas agrupadas por dono, na ordem em que chegaram.
   *
   * O cooperado com subordinados vê as faturas deles: a rota expande a
   * hierarquia. Sem o cabeçalho de grupo, o valor de outra pessoa apareceria
   * solto, como se fosse dele.
   */
  const porMotorista = faturas.reduce<Map<number, FaturaCte[]>>((acc, f) => {
    const lista = acc.get(f.idMotorista) ?? [];
    lista.push(f);
    acc.set(f.idMotorista, lista);
    return acc;
  }, new Map());

  return {
    faturas, previas, total, carregando, erro, recarregar: carregar,
    porMotorista,
    temMultiplosMotoristas: porMotorista.size > 1,
  };
}
